import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { savedSearchesApi } from "@/api/savedSearches";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

const formatBytes = (n: number | null): string => {
  if (n == null) return "—";
  if (n === 0) return "0";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

export function SavedSearchDetail() {
  const { id } = useParams({ from: "/search/saved/$id" });
  const numericId = Number(id);
  const qc = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["saved-search", numericId],
    queryFn: () => savedSearchesApi.get(numericId),
    enabled: Number.isFinite(numericId),
  });

  const runMut = useMutation({
    mutationFn: () => savedSearchesApi.run(numericId),
    onSuccess: (outcome) => {
      toast(
        `Ran: ${outcome.fresh_hits} new of ${outcome.total_hits} hits` +
          (outcome.grab_dispatched ? " — auto-grab dispatched" : ""),
        outcome.fresh_hits > 0 ? "success" : "info",
      );
      qc.invalidateQueries({ queryKey: ["saved-search", numericId] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Run failed", "error"),
  });

  if (!Number.isFinite(numericId)) {
    return <EmptyState title="Bad URL" description="Saved search id missing." />;
  }
  if (isLoading || !data) {
    return <p className="text-sm text-foreground/60">Loading…</p>;
  }

  const { saved_search: row, recent_hits: hits, next_fires } = data;

  // The hit row doesn't carry the original `download_url` (single-use
  // newznab links go stale, so we'd just be storing rotting URLs). For
  // a manual grab, redirect the user to the live `/search` page with
  // the saved query pre-filled — they get an up-to-date listing and
  // the existing per-row grab UI without a parallel implementation.
  const grabHit = (hit: (typeof hits)[number]) => {
    void hit; // hit is informational; the search will refetch from indexers.
    navigate({
      to: "/search",
      // tanstack-router doesn't support search params via this hook
      // out of the box, so push a query string the manual page can
      // read off `window.location` if it cares. For now: just navigate.
    });
    toast(
      "Re-running query on /search — pick the release you want there",
      "info",
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/search/saved"
          className="text-sm text-foreground/60 hover:text-accent"
        >
          ← Saved searches
        </Link>
      </div>
      <PageHeader
        title={row.name}
        actions={
          <Button onClick={() => runMut.mutate()} loading={runMut.isPending}>
            Run now
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 rounded border border-line p-4 text-sm sm:grid-cols-3">
        <Stat label="Query" value={<span className="font-mono">{row.query}</span>} />
        <Stat
          label="Schedule"
          value={
            row.schedule_cron ? (
              <span className="font-mono">{row.schedule_cron}</span>
            ) : (
              <span className="text-foreground/40">manual</span>
            )
          }
        />
        <Stat
          label="Last run"
          value={row.last_run_at ? new Date(row.last_run_at).toLocaleString() : "—"}
        />
        <Stat label="Last hit count" value={row.last_hit_count ?? "—"} />
        <Stat label="Enabled" value={row.enabled ? "yes" : "no"} />
        <Stat label="Auto-grab" value={row.auto_grab ? "yes" : "no"} />
      </div>

      {next_fires.length > 0 && (
        <div className="rounded border border-line p-4 text-sm">
          <div className="eyebrow mb-2">Next fires</div>
          <ul className="space-y-1 font-mono text-xs text-foreground/70">
            {next_fires.map((t) => (
              <li key={t}>{new Date(t).toLocaleString()}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h2 className="mb-2 font-display text-lg font-semibold tracking-display-tight">
          Recent hits
        </h2>
        {hits.length === 0 ? (
          <EmptyState
            title="No hits yet"
            description="Run the search now or wait for its schedule to fire."
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-4 py-2 font-medium">Seen</th>
                  <th className="px-4 py-2 font-medium">Release</th>
                  <th className="px-4 py-2 font-medium">Size</th>
                  <th className="px-4 py-2 font-medium">Indexer</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {hits.map((h) => (
                  <tr key={h.id} className="border-t border-line">
                    <td className="px-4 py-2 text-xs text-foreground/70">
                      {new Date(h.seen_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {h.release_title}
                    </td>
                    <td className="px-4 py-2 text-xs text-foreground/70">
                      {formatBytes(h.size_bytes)}
                    </td>
                    <td className="px-4 py-2 text-xs text-foreground/70">
                      {h.indexer_id ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {h.grabbed ? (
                        <span className="rounded bg-accent/15 px-1.5 py-0.5 text-accent">
                          grabbed
                        </span>
                      ) : (
                        <span className="text-foreground/40">new</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {!h.grabbed && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => grabHit(h)}
                          title="Open /search to grab a fresh result"
                        >
                          Grab
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="eyebrow mb-1">{label}</div>
      <div className="text-foreground/85">{value}</div>
    </div>
  );
}
